import React, { useState, useCallback, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

let toastIdCounter = 0;

const toastState = {
  toasts: [] as Toast[],
  listeners: new Set<(toasts: Toast[]) => void>(),
};

const notify = () => {
  toastState.listeners.forEach((listener) => {
    listener(toastState.toasts);
  });
};

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>(toastState.toasts);

  // Suscribirse a cambios
  useEffect(() => {
    const listener = (newToasts: Toast[]) => {
      setToasts(newToasts);
    };
    toastState.listeners.add(listener);

    return () => {
      toastState.listeners.delete(listener);
    };
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string, duration = 5000) => {
      const id = `toast-${++toastIdCounter}`;
      const newToast: Toast = { id, type, message, duration };

      toastState.toasts = [...toastState.toasts, newToast];
      notify();

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    toastState.toasts = toastState.toasts.filter((toast) => toast.id !== id);
    notify();
  }, []);

  const clearAllToasts = useCallback(() => {
    toastState.toasts = [];
    notify();
  }, []);

  return {
    toasts: toasts || [],
    addToast,
    removeToast,
    clearAllToasts,
  };
};
